/**
 * Who changed a report's commercials, and when — shown two ways: a one-line
 * "last changed by …" beside a single value, and a collapsible list of every
 * change. Both take the actor-name lookup from the caller (the analyst
 * roster), so they stay plain presentation.
 */

import { useState } from 'react';

import { Button, Collapse, Stack, Text } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';

import type { CommercialChange } from '@/types/commercial-change';
import { describeChange, describeLastChange } from '@/utils/commercialChanges';

type NameFor = (userId: string) => string;

interface LastChangedLineProps {
  change: CommercialChange | null | undefined;
  nameFor?: NameFor;
  /** Which value it is about, when several lines sit together ("Price"). */
  label?: string;
}

/** "Last changed by Alice on 12 Oct 2026 — “Client agreed a revised quote”".
 * Renders nothing for a value that has never been changed. */
export function LastChangedLine({ change, nameFor, label }: Readonly<LastChangedLineProps>) {
  if (!change) {
    return null;
  }
  return (
    <Text size="xs" c="dimmed" data-testid="last-changed">
      {label ? `${label} · ` : ''}
      {describeLastChange(change, nameFor)}
      {change.reason ? ` — “${change.reason}”` : ''}
    </Text>
  );
}

interface CommercialHistoryListProps {
  /** Newest first, as the API returns them. */
  changes: readonly CommercialChange[];
  nameFor?: NameFor;
}

/** A collapsed "Change history (n)" that opens to every recorded change. */
export function CommercialHistoryList({ changes, nameFor }: Readonly<CommercialHistoryListProps>) {
  const [open, setOpen] = useState(false);
  if (changes.length === 0) {
    return null;
  }
  return (
    <Stack gap={4} data-testid="commercial-history">
      <Button
        variant="subtle"
        size="compact-xs"
        justify="flex-start"
        leftSection={open ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        data-testid="commercial-history-toggle"
      >
        Change history ({changes.length})
      </Button>
      <Collapse in={open}>
        <Stack gap={6}>
          {changes.map((change) => (
            <Stack key={change.id} gap={0} data-testid="commercial-history-row">
              <Text size="xs">{describeChange(change, nameFor)}</Text>
              <LastChangedLine change={change} nameFor={nameFor} />
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}
